import * as React from "react";

import type { Stop } from "../types";
import type { ActiveSelection } from "../atoms/activeSelection";

import { rootIdOf } from "../components/Map/transitMap/helpers";
import { STOP_LOCATIONS_AUTO_ZOOM } from "../components/Map/transitMap/constants";

type Args = {
  mapRef: React.MutableRefObject<google.maps.Map | null>;

  allStops: Stop[];

  stopIds: string[];
  setStopIds: (ids: string[]) => void;

  activeStopId: string | null;
  activeStopParam: string | null;

  expanded: Set<string>;
  openSidebarId: string | null;

  setActiveStopId: (id: string | null) => void;
  setActiveStopParam: (id: string | null) => void;
  setActiveSelection: (selection: ActiveSelection | null) => void;

  toggleRoot: (rootId: string, checked: boolean) => Promise<void>;

  toggleSidebar: (id: "stopAndLines") => void;

  markSeen: (id: string | number) => void;

  stopAllLive: () => void;

  setShowLiveByLine: React.Dispatch<React.SetStateAction<Set<string>>>;
};

export function useTransitStopSelection({
  mapRef,

  allStops,

  stopIds,
  setStopIds,

  activeStopId,
  activeStopParam,

  expanded,
  openSidebarId,

  setActiveStopId,
  setActiveStopParam,
  setActiveSelection,

  toggleRoot,
  toggleSidebar,

  markSeen,

  stopAllLive,
  setShowLiveByLine,
}: Args) {
  const suppressActiveStopFromUrlRef = React.useRef(false);

  const stopIdsRef = React.useRef<string[]>(stopIds);

  const activeStopIdRef = React.useRef<string | null>(activeStopId);

  const activeStopParamRef = React.useRef<string | null>(activeStopParam);

  const cameraAnimationRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    stopIdsRef.current = stopIds;
  }, [stopIds]);

  React.useEffect(() => {
    activeStopIdRef.current = activeStopId;
  }, [activeStopId]);

  React.useEffect(() => {
    activeStopParamRef.current = activeStopParam;
  }, [activeStopParam]);

  React.useEffect(() => {
    return () => {
      if (cameraAnimationRef.current !== null) {
        window.cancelAnimationFrame(cameraAnimationRef.current);
        cameraAnimationRef.current = null;
      }
    };
  }, []);

  const suppressActiveStopUrlSyncOnce = React.useCallback(() => {
    suppressActiveStopFromUrlRef.current = true;

    window.setTimeout(() => {
      suppressActiveStopFromUrlRef.current = false;
    }, 0);
  }, []);

  const clearActiveStop = React.useCallback(() => {
    suppressActiveStopUrlSyncOnce();

    activeStopIdRef.current = null;
    activeStopParamRef.current = null;

    setActiveSelection(null);
    setActiveStopId(null);

    setActiveStopParam(null);
  }, [
    suppressActiveStopUrlSyncOnce,
    setActiveSelection,
    setActiveStopId,
    setActiveStopParam,
  ]);

  const animateStopFocus = React.useCallback(
    (
      map: google.maps.Map,
      target: google.maps.LatLngLiteral,
      finalZoom: number,
      startZoomOverride?: number
    ) => {
      if (cameraAnimationRef.current !== null) {
        window.cancelAnimationFrame(cameraAnimationRef.current);
        cameraAnimationRef.current = null;
      }

      const currentCenter = map.getCenter();

      if (!currentCenter) {
        map.panTo(target);
        return;
      }

      const startCenter = {
        lat: currentCenter.lat(),
        lng: currentCenter.lng(),
      };

      const duration = 500;

      const startZoom =
        startZoomOverride != null
          ? Math.min(startZoomOverride, finalZoom)
          : finalZoom;

      const startTime = performance.now();

      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

      const animate = (now: number) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = easeOutCubic(progress);

        const lat = startCenter.lat + (target.lat - startCenter.lat) * eased;

        const lng = startCenter.lng + (target.lng - startCenter.lng) * eased;

        const zoom = startZoom + (finalZoom - startZoom) * eased;

        map.moveCamera({
          center: {
            lat,
            lng,
          },
          zoom,
        });

        if (progress < 1) {
          cameraAnimationRef.current = window.requestAnimationFrame(animate);
        } else {
          cameraAnimationRef.current = null;

          map.moveCamera({
            center: target,
            zoom: finalZoom,
          });
        }
      };

      cameraAnimationRef.current = window.requestAnimationFrame(animate);
    },
    []
  );

  const selectStopGroupFromId = React.useCallback(
    (
      stopId: string,
      fallbackStop?: {
        lat: number;
        lng: number;
      }
    ) => {
      const rootId = rootIdOf(stopId);

      setShowLiveByLine(new Set());
      stopAllLive();

      const currentActiveRootId = activeStopIdRef.current
        ? rootIdOf(activeStopIdRef.current)
        : null;

      if (
        currentActiveRootId &&
        currentActiveRootId !== rootId &&
        expanded.has(currentActiveRootId)
      ) {
        void toggleRoot(currentActiveRootId, false);
      }

      const group = allStops.filter((stop) => rootIdOf(stop.id) === rootId);

      const panTarget = group[0] ?? fallbackStop;

      if (!panTarget) return;

      if (mapRef.current) {
        const map = mapRef.current;

        const target = {
          lat: panTarget.lat,
          lng: panTarget.lng,
        };

        const currentZoom = map.getZoom() ?? 0;

        const stopSelectZoom = STOP_LOCATIONS_AUTO_ZOOM;

        if (currentZoom < stopSelectZoom) {
          animateStopFocus(map, target, stopSelectZoom, currentZoom);
        } else {
          animateStopFocus(map, target, currentZoom);
        }
      }

      const groupIds =
        group.length > 0
          ? group.map((stop) => String(stop.id))
          : [String(stopId)];

      const nextStopIds = Array.from(
        new Set([...stopIdsRef.current, ...groupIds])
      );

      stopIdsRef.current = nextStopIds;

      setStopIds(nextStopIds);

      const activeId = rootId;

      activeStopIdRef.current = activeId;
      activeStopParamRef.current = activeId;

      setActiveStopId(activeId);

      setActiveSelection({
        kind: "stop",
        id: activeId,
      });

      setActiveStopParam(activeId);

      markSeen(activeId);

      if (openSidebarId !== "stopAndLines") {
        toggleSidebar("stopAndLines");
      }
    },
    [
      allStops,
      animateStopFocus,
      expanded,
      mapRef,
      markSeen,
      openSidebarId,
      setActiveSelection,
      setActiveStopId,
      setActiveStopParam,
      setShowLiveByLine,
      setStopIds,
      stopAllLive,
      toggleRoot,
      toggleSidebar,
    ]
  );

  const handleStopMarkerClick = React.useCallback(
    (stop: Stop) => {
      const clickedRootId = rootIdOf(stop.id);

      const activeRootId = activeStopIdRef.current
        ? rootIdOf(activeStopIdRef.current)
        : null;

      if (activeRootId === clickedRootId) {
        clearActiveStop();

        if (expanded.has(clickedRootId)) {
          void toggleRoot(clickedRootId, false);
        }

        return;
      }

      selectStopGroupFromId(stop.id);
    },
    [clearActiveStop, expanded, selectStopGroupFromId, toggleRoot]
  );

  const handleRemoveStop = React.useCallback(
    (id: string) => {
      const rootId = rootIdOf(id);

      const currentStopIds = stopIdsRef.current;

      const currentActiveStopId = activeStopIdRef.current;

      const currentActiveStopParam = activeStopParamRef.current;

      const nextStopIds = currentStopIds.filter(
        (stopId) => rootIdOf(stopId) !== rootId
      );

      stopIdsRef.current = nextStopIds;

      setStopIds(nextStopIds);

      const removedActiveStop =
        currentActiveStopId != null && rootIdOf(currentActiveStopId) === rootId;

      const removedActiveStopParam =
        currentActiveStopParam != null &&
        rootIdOf(currentActiveStopParam) === rootId;

      if (removedActiveStop || removedActiveStopParam) {
        suppressActiveStopUrlSyncOnce();

        activeStopIdRef.current = null;
        activeStopParamRef.current = null;

        setActiveSelection(null);
        setActiveStopId(null);

        setActiveStopParam(null);
      }

      if (expanded.has(rootId)) {
        void toggleRoot(rootId, false);
      }
    },
    [
      expanded,
      setActiveSelection,
      setActiveStopId,
      setActiveStopParam,
      setStopIds,
      suppressActiveStopUrlSyncOnce,
      toggleRoot,
    ]
  );

  const handleSetActiveStopId = React.useCallback(
    (id: string | null) => {
      activeStopIdRef.current = id;
      activeStopParamRef.current = id;

      setActiveStopId(id);

      if (id) {
        setActiveSelection({
          kind: "stop",
          id,
        });
      } else {
        setActiveSelection(null);
      }

      setActiveStopParam(id);
    },
    [setActiveSelection, setActiveStopId, setActiveStopParam]
  );

  return {
    suppressActiveStopUrlSyncOnce,
    selectStopGroupFromId,
    handleStopMarkerClick,
    handleRemoveStop,
    handleSetActiveStopId,
  };
}
